// Plantillas de informe y maestro de ítems sobre TanStack Query. La caché es la
// única fuente del listado; las mutaciones invalidan en vez de parchear.
//
// Qué invalida qué (se cruzan más de lo que parece):
// - Guardar la ESTRUCTURA de una plantilla puede pasar ítems al maestro y
//   cambiar los conteos del listado → plantillas + maestro.
// - Editar un ÍTEM DEL MAESTRO con `uso` mueve ítems entre plantillas → todo
//   el árbol de `plantillas` (listado y detalles abiertos) + maestro.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarMaestroItemRequest,
  actualizarPlantillaRequest,
  crearMaestroItemRequest,
  crearPlantillaRequest,
  fetchContenidosDeLaPlantilla,
  fetchMaestroItems,
  fetchPlantilla,
  fetchPlantillas,
  guardarEstructuraRequest,
  type CambioDeUso,
  type DatosEditables,
  type EstructuraAGuardar,
  type ItemDelMaestroEditable,
  type MaestroItemConUso,
} from "@/lib/plantillas";

export const PLANTILLAS_KEY = ["plantillas"] as const;
export const MAESTRO_KEY = ["maestro-items"] as const;

export function usePlantillas() {
  return useQuery({ queryKey: PLANTILLAS_KEY, queryFn: fetchPlantillas });
}

export function usePlantilla(id: number) {
  return useQuery({
    queryKey: [...PLANTILLAS_KEY, id],
    queryFn: () => fetchPlantilla(id),
    enabled: Number.isFinite(id),
  });
}

export function useMaestroItems() {
  return useQuery({ queryKey: MAESTRO_KEY, queryFn: fetchMaestroItems });
}

/**
 * Los ítems de una plantilla para armar un informe. Disponible para cualquier
 * usuario autenticado (no es el detalle del mantenedor).
 */
export function useContenidosDeLaPlantilla(id: number | null) {
  return useQuery({
    queryKey: [...PLANTILLAS_KEY, id, "contenidos"],
    queryFn: () => fetchContenidosDeLaPlantilla(id as number),
    enabled: id !== null,
  });
}

export function useCrearPlantilla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (input: DatosEditables & { base_id?: number | null }) =>
      crearPlantillaRequest(input),
    onSuccess: () => cliente.invalidateQueries({ queryKey: PLANTILLAS_KEY }),
  });
}

export function useActualizarPlantilla(id: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (input: DatosEditables) => actualizarPlantillaRequest(id, input),
    onSuccess: (detalle) => {
      cliente.setQueryData([...PLANTILLAS_KEY, id], detalle);
      cliente.invalidateQueries({ queryKey: PLANTILLAS_KEY, exact: true });
    },
  });
}

export function useGuardarEstructura(id: number) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (estructura: EstructuraAGuardar) => guardarEstructuraRequest(id, estructura),
    onSuccess: (detalle) => {
      cliente.setQueryData([...PLANTILLAS_KEY, id], detalle);
      cliente.invalidateQueries({ queryKey: PLANTILLAS_KEY, exact: true });
      cliente.invalidateQueries({ queryKey: MAESTRO_KEY });
    },
  });
}

export function useCrearMaestroItem() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (input: ItemDelMaestroEditable) => crearMaestroItemRequest(input),
    onSuccess: () => cliente.invalidateQueries({ queryKey: MAESTRO_KEY }),
  });
}

export function useActualizarMaestroItem() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: ItemDelMaestroEditable & { id: number; uso?: CambioDeUso }) =>
      actualizarMaestroItemRequest(id, input),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: MAESTRO_KEY });
      cliente.invalidateQueries({ queryKey: PLANTILLAS_KEY });
    },
  });
}

/** Aplica un cambio de uso (`null` saca la plantilla) sobre el mapa de un ítem. */
function conElUsoCambiado(uso: MaestroItemConUso["uso"], cambio: CambioDeUso): MaestroItemConUso["uso"] {
  const siguiente = { ...uso };
  for (const [plantilla, valor] of Object.entries(cambio)) {
    if (valor === null) delete siguiente[plantilla];
    else siguiente[plantilla] = valor;
  }
  return siguiente;
}

/**
 * Cambia, desde el cajón de una plantilla, el estado de un ítem en OTRA
 * plantilla. Se guarda en el acto —el servidor escribe las filas de esas
 * otras plantillas, nunca el borrador de la que se está editando— y la
 * pantalla se mueve de inmediato: si el guardado falla, el estado vuelve al
 * que tenía.
 */
export function useCambiarUsoEnOtraPlantilla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: ItemDelMaestroEditable & { id: number; uso: CambioDeUso }) =>
      actualizarMaestroItemRequest(id, input),
    onMutate: async ({ id, uso }) => {
      await cliente.cancelQueries({ queryKey: MAESTRO_KEY });
      const anterior = cliente.getQueryData<MaestroItemConUso[]>(MAESTRO_KEY);
      cliente.setQueryData<MaestroItemConUso[]>(MAESTRO_KEY, (lista) =>
        lista?.map((item) =>
          item.id === id ? { ...item, uso: conElUsoCambiado(item.uso, uso) } : item,
        ),
      );
      return { anterior };
    },
    onError: (_error, _variables, contexto) => {
      if (contexto?.anterior) cliente.setQueryData(MAESTRO_KEY, contexto.anterior);
    },
    onSettled: () => {
      cliente.invalidateQueries({ queryKey: MAESTRO_KEY });
      cliente.invalidateQueries({ queryKey: PLANTILLAS_KEY });
    },
  });
}
